import { ThemeModeComponent } from '@_metronic/assets/ts/layout'
import React, {useMemo} from 'react'
import Select, {StylesConfig} from 'react-select'
import {StateManagerProps} from 'react-select/dist/declarations/src/useStateManager'

type ThemedSelectProps = StateManagerProps & {
  style?: any
  isFirstElementButton?: boolean
}

const systemMode = ThemeModeComponent.getSystemMode() as 'light' | 'dark'

const ThemedSelect = ({style, isFirstElementButton = false, ...props}: ThemedSelectProps) => {
  const themedStyle = useMemo(() => {
    const backgroundColor = 'var(--bs-body-bg)' // darkMode ? DARK_COLOR : LIGHT_COLOR
    const hoverBackgroundColor = 'var(--bs-primary)'
    const hoverTextColor = 'white'

    const colourStyles: StylesConfig = {
      control: (styles) => ({
        ...styles,
        backgroundColor: backgroundColor,
        borderColor: 'var(--bs-gray-300)',
        opacity: props.isDisabled ? 0.3 : 1,
      }),
      option: (styles, {data, isDisabled, isFocused, isSelected, options, getValue}) => {
        const index = options.findIndex((option) => option === data)

        if (isFirstElementButton && index === 0) {
          return {
            ...styles,
            color: 'var(--bs-primary)',
            cursor: 'pointer',
            ':hover': {
              backgroundColor,
              color: 'var(--bs-primary)',
            },
            '&:focus': {
              backgroundColor: backgroundColor,
              boxShadow: 'none',
              outline: 'none',
            },
          }
        }

        return {
          ...styles,
          backgroundColor: isDisabled
            ? undefined
            : isFocused
            ? hoverBackgroundColor
            : isSelected
            ? backgroundColor
            : backgroundColor,
          color: isDisabled ? undefined : isFocused ? hoverTextColor : undefined,
          cursor: isDisabled ? 'not-allowed' : 'pointer',

          ':active': {
            ...styles[':active'],
            backgroundColor: !isDisabled
              ? isSelected
                ? backgroundColor
                : `${backgroundColor}60`
              : undefined,
          },
        }
      },
      input: (styles) => ({
        ...styles,
        color: 'var(--bs-gray-700)',
        fontSize: '1.1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      }),
      placeholder: (styles) => ({
        ...styles,
        color: 'var(--bs-gray-700)',
        fontSize: '1.1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      }),
      singleValue: (styles) => {
        const defaultColor = 'var(--bs-gray-700)'
        const lightColor = '#ccc'

        return {
          ...styles,
          color: systemMode === 'dark' ? lightColor : defaultColor,
        }
      },
      menu: (styles) => ({...styles, backgroundColor}),
      indicatorSeparator: (styles) => ({...styles, backgroundColor: 'var(--bs-gray-300)'}),
      dropdownIndicator: (styles) => ({
        ...styles,
        color: 'var(--bs-gray-500)',
        width: '35px',
        marginRight: '3px',
      }),
    }

    return colourStyles
  }, [])

  return <Select styles={{...themedStyle, ...style}} {...props} />
}

export default React.memo(ThemedSelect)
